/**
 * /funny/:id — single comic detail page.
 * Same hero treatment as the main funny page but for one specific comic.
 */

import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { useAuth } from '../contexts/AuthContext.jsx';
import { usePageTitle } from '../hooks/usePageTitle.js';
import { getComic, getComicReactions, reactToComic } from '../api/comics.js';
import SiteHeader from '../components/SiteHeader.jsx';
import ComicViewer from '../components/comics/ComicViewer.jsx';
import ReactionBar from '../components/tracks/ReactionBar.jsx';
import CommentsSection from '../components/tracks/CommentsSection.jsx';
import './funny.css';


export default function ComicDetailPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const isAuth = Boolean(user);

  const [comic, setComic] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [counts, setCounts] = useState({});
  const [mine, setMine] = useState([]);

  usePageTitle(comic ? comic.title : 'The Funny Page');

  const loadComic = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const c = await getComic(id);
      setComic(c);

      const reacts = await getComicReactions(c.id);
      setCounts(reacts.counts || {});
      setMine(reacts.my_reactions || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadComic();
  }, [loadComic, isAuth]);

  async function handleReact(emoji) {
    if (!comic) return;
    const result = await reactToComic(comic.id, emoji);
    setCounts(result.counts);
    setMine((prev) =>
      result.active ? [...prev, emoji] : prev.filter((e) => e !== emoji)
    );
  }

  return (
    <div className="funny-page">
      <SiteHeader adminHref="/admin/comics" />

      <header className="funny-header">
        <Link to="/funny" className="funny-back-link">← back to the funny page</Link>
      </header>

      {loading && <div className="funny-loading">loading...</div>}
      {error && <div className="funny-error">something broke. text Tim.</div>}

      {comic && (
        <section className="funny-hero">
          <div className="funny-hero__comic">
            <ComicViewer src={comic.file_path} alt={comic.title} />
          </div>

          <div className="funny-hero__meta">
            <h2 className="funny-hero__title">{comic.title}</h2>
            {comic.description && (
              <p className="funny-hero__desc">{comic.description}</p>
            )}
          </div>

          <div className="funny-hero__reactions">
            <ReactionBar
              counts={counts}
              myReactions={mine}
              onToggle={handleReact}
              isAuthenticated={isAuth}
            />
          </div>

          <div className="funny-hero__comments">
            <CommentsSection
              targetType="comic"
              targetId={comic.id}
            />
          </div>
        </section>
      )}
    </div>
  );
}
